import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { copy } from "../../../data/copy";
import RotatingWord from "./components/RotatingWord";
import RotatingImage from "./components/RotatingImage";

const IMAGE_GROUPS = {
  first: ["/assets/home/hero/img4-160.webp", "/assets/home/hero/img1-160.webp", "/assets/home/hero/img2-160.webp"],
  second: ["/assets/home/hero/img3-160.webp", "/assets/home/hero/img5-160.webp", "/assets/home/hero/img6-160.webp"],
  third: ["/assets/home/hero/img2-160.webp", "/assets/home/hero/img7-160.webp", "/assets/home/hero/img9-160.webp"],
  fourth: ["/assets/home/hero/img1-160.webp", "/assets/home/hero/img14-160.webp", "/assets/home/hero/img13-160.webp"],
};

function Hero() {
  const [wordIndex, setWordIndex] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);
  const [isVideoActive, setIsVideoActive] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [animationStarted, setAnimationStarted] = useState(false);

  const {
    clarityWords,
    focusWords,
    adhdWords,
    anxietyWords,
    depressionWords,
    subheadline,
    subheadlineEnd,
    cta,
  } = copy.hero;

  useEffect(() => {
    // 1. Initial entrance animation trigger
    const entranceTimer = setTimeout(() => setAnimationStarted(true), 100);

    // 2. Words rotate every 5 seconds
    const wordTimer = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % clarityWords.length);
    }, 5000);

    // 3. Images rotate every 6 seconds
    const imageTimer = setInterval(() => {
      setImageIndex((prev) => (prev + 1) % 3);
    }, 6000);

    return () => {
      clearTimeout(entranceTimer);
      clearInterval(wordTimer);
      clearInterval(imageTimer);
    };
  }, [clarityWords.length]);

  const toggleVideoPlayback = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused || video.ended) {
      void video.play();
    } else {
      video.pause();
    }
  };

  return (
    <section
      className="relative box-border px-4 sm:px-5 md:px-6 lg:px-8 xl:px-40 2xl:px-64 min-h-[100dvh] flex items-center justify-center pt-[var(--site-header-height)] pb-12 sm:pb-16 md:pb-20"
    >
      <div className="flex flex-col lg:flex-row relative w-full justify-between items-center gap-6 sm:gap-12 lg:gap-8">
        {/* Text Content */}
        <div className="text-center md:text-left max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl lg:w-[60%] xl:w-2/3 mx-auto lg:mx-0 order-1 relative z-10">
          
          {/* Clean typography headline with dynamic words and images */}
          <h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-4xl xl:text-6xl 2xl:text-7xl font-extrabold leading-[1.3] lg:leading-[1.4] mb-3 sm:mb-6 text-gray-900"
          >
            <div className="flex flex-wrap items-end justify-center md:justify-start gap-y-3 gap-x-2 sm:gap-x-3">
              <span>A <span className="font-serif italic font-medium text-orange-400">Better</span></span>
              <RotatingImage 
                images={IMAGE_GROUPS.first} 
                imageIndex={imageIndex} 
                className="w-12 h-12 sm:w-14 sm:h-14 lg:w-11 lg:h-11 xl:w-16 xl:h-16 2xl:w-20 2xl:h-20 rounded-full" 
              />
              <span>Life</span>
              <div className="hidden sm:block basis-full h-0"></div>
              <span>Starts with</span>
              <RotatingImage 
                images={IMAGE_GROUPS.second} 
                imageIndex={imageIndex} 
                className="w-12 h-12 sm:w-14 sm:h-14 lg:w-11 lg:h-11 xl:w-16 xl:h-16 2xl:w-20 2xl:h-20 rounded-full" 
              />
              <RotatingWord 
                words={clarityWords} 
                currentIndex={wordIndex} 
                className="font-serif italic font-medium text-blue-600 underline decoration-wavy decoration-blue-500/40 underline-offset-4" 
              />
              <span>and</span>
              <RotatingImage 
                images={IMAGE_GROUPS.third} 
                imageIndex={imageIndex} 
                className="w-12 h-12 sm:w-14 sm:h-14 lg:w-11 lg:h-11 xl:w-16 xl:h-16 2xl:w-20 2xl:h-20 rounded-full" 
              />
              <div className="inline-flex items-baseline">
                <RotatingWord 
                  words={focusWords} 
                  currentIndex={wordIndex} 
                  className="font-serif italic font-medium text-rose underline decoration-wavy decoration-rose-500/40 underline-offset-4" 
                />
                <span>.</span>
              </div>
            </div>
          </h1>

          {/* Stable Height Description Container to prevent layout shifts */}
          <div className="relative w-full mt-3 sm:mt-5 md:mt-6 lg:mt-5 xl:mt-8">
            {/* Invisible Spacer Paragraph - rendering the longest terms to reserve maximum height */}
            <p 
              className="invisible pointer-events-none select-none text-base sm:text-lg lg:text-base xl:text-xl leading-relaxed text-transparent"
              aria-hidden="true"
            >
              {subheadline}{" "}
              <span className="font-extrabold text-gray-900 inline-block whitespace-nowrap">Sexual Dysfunction,</span>{" "}
              <span className="font-extrabold text-gray-900 inline-block whitespace-nowrap">Obsessive Compulsive Disorder,</span>{" "}
              or{" "}
              <span className="font-extrabold text-gray-900 inline-block whitespace-nowrap">Personality Disorders,</span>
              {subheadlineEnd.replace(/^,/, "")}
            </p>

            {/* Visible Rotating Paragraph */}
            <p 
              className="absolute inset-0 text-base sm:text-lg lg:text-base xl:text-xl leading-relaxed text-gray-700 text-center md:text-left"
            >
              {subheadline}{" "}
              <RotatingWord words={adhdWords} currentIndex={wordIndex} suffix="," className="font-extrabold text-gray-900" />{" "}
              <RotatingWord words={anxietyWords} currentIndex={wordIndex} suffix="," className="font-extrabold text-gray-900" />{" "}
              or{" "}
              <RotatingWord words={depressionWords} currentIndex={wordIndex} suffix="," className="font-extrabold text-gray-900" />
              {subheadlineEnd.replace(/^,/, "")}
            </p>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-center gap-6 mt-4 sm:mt-8 justify-center md:justify-start">
            <Link to="/booking" className="btn btn-primary btn-lg">
              {cta}
            </Link>
          </div>
        </div>

        {/* Capsule Video Frame & Floating Cards (Premium style) */}
        <div 
          className={`hidden w-full sm:flex sm:w-4/5 md:w-3/5 lg:w-[40%] xl:w-[33%] mx-auto lg:mx-0 lg:ml-auto order-2 mt-4 lg:mt-0 flex-col lg:block items-center justify-center relative transition-all duration-1000 ${
            animationStarted ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
          } z-20`}
        >
          {/* Main Capsule Frame with Video */}
          <div className="group relative w-full max-w-[280px] lg:max-w-[350px] aspect-[1/1.32] rounded-[12rem] border-[8px] border-white/60 shadow-[0_30px_70px_-15px_rgba(15,23,42,0.12)] overflow-hidden bg-black/5 backdrop-blur-sm transition-all duration-500 hover:shadow-[0_40px_80px_-10px_rgba(15,23,42,0.15)]">
            {isVideoActive ? (
              <>
                <video
                  ref={videoRef}
                  src="/assets/home/hero/introduction-video.webm"
                  autoPlay
                  playsInline
                  onPlay={() => setIsVideoPlaying(true)}
                  onPause={() => setIsVideoPlaying(false)}
                  onEnded={() => setIsVideoPlaying(false)}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={toggleVideoPlayback}
                  className="absolute inset-0 z-10 cursor-pointer"
                  aria-label={isVideoPlaying ? "Pause introduction video" : "Play introduction video"}
                >
                  <span className="sr-only">
                    {isVideoPlaying ? "Pause video" : "Play video"}
                  </span>
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setIsVideoActive(true)}
                className="relative h-full w-full"
                aria-label="Play introduction video"
              >
                <span className="flex h-full w-full items-center justify-center bg-white p-8 sm:p-10">
                  <img
                    src="/assets/home/adhd-logo-240.webp"
                    width={240}
                    height={85}
                    fetchPriority="high"
                    decoding="async"
                    alt="ADHD-AID Psychiatry"
                    className="w-full max-w-[15rem] transition-transform duration-700 group-hover:scale-105"
                  />
                </span>
                <span className="absolute inset-0 flex items-center justify-center bg-slate-950/5">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-blue-600 shadow-xl backdrop-blur-sm">
                    <svg
                      viewBox="0 0 24 24"
                      className="ml-1 h-6 w-6"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                </span>
              </button>
            )}
          </div>

          {/* Floating Card: Personalized Care (Top Right) */}
          <div className="absolute top-4 -right-2 sm:top-6 sm:-right-6 lg:top-10 lg:-right-8 bg-white/70 backdrop-blur-md rounded-2xl lg:rounded-3xl p-3 lg:p-5 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-white/60 w-[130px] lg:w-[170px] z-30 hidden sm:flex flex-col items-start transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_8px_40px_rgb(0,0,0,0.1)]">
            <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-gradient-to-tr from-blue-500 to-blue-400 flex items-center justify-center mb-2 lg:mb-3 shadow-lg shadow-blue-500/20">
              <svg className="w-4 h-4 lg:w-5 lg:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="text-2xl lg:text-3xl font-bold text-slate-800 tracking-tight leading-none mb-1">100%</span>
            <span className="text-xs lg:text-sm font-medium text-slate-500 leading-snug">
              Personalized care
            </span>
          </div>

          {/* Floating Card: Online Status & Message (Bottom Left) */}
          <div className="absolute bottom-12 -left-2 sm:bottom-10 sm:-left-8 lg:bottom-10 lg:-left-12 bg-white/80 backdrop-blur-xl rounded-full pl-1.5 pr-4 py-1.5 lg:pl-2 lg:pr-6 lg:py-2 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/60 hidden sm:flex items-center gap-2 lg:gap-4 z-30 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_8px_40px_rgb(0,0,0,0.12)]">
            <div className="relative shrink-0">
              <div className="w-9 h-9 lg:w-12 lg:h-12 rounded-full overflow-hidden border-2 border-white shadow-sm">
                <img src="/assets/home/about/brandon-burr-96.webp" width={48} height={48} loading="lazy" decoding="async" className="w-full h-full object-cover" alt="Brandon Burr" />
              </div>
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 lg:w-3.5 lg:h-3.5 bg-cerulean border-2 border-white rounded-full shadow-sm" />
            </div>
            <div className="flex flex-col justify-center">
              <span className="text-[9px] lg:text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                Online Now
              </span>
              <span className="text-xs lg:text-sm font-bold text-slate-800 leading-none">
                Brandon Burr, NP
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
